/**
 * Search Filters Component
 * Manages filter UI interactions and integrates with the search store
 */

const searchFilters = (settings = {}) => {
  return {
    settings,
    openDropdowns: new Set(),
    openAccordions: new Set(),
    metaobjectSwatchCache: new Map(),
    customSwatchFilterId: null,
    customSwatchMetaobject: null,
    
    /**
     * Initialize the component
     */
    init() {
      // Get custom swatch settings from element data attributes
      this.customSwatchFilterId = this.$el.dataset.customSwatchFilter;
      this.customSwatchMetaobject = this.$el.dataset.customSwatchMetaobject;
      
      // Initialize custom swatch cache if settings are configured
      if (this.customSwatchFilterId && this.customSwatchMetaobject) {
        this.initCustomSwatchCache();
      }
      
      // Watch for changes in search store filters
      this.$watch('$store.search.filters', (newFilters) => {
        this.setupFilters(newFilters);
      });
      
      // Watch for applied filters changes to update UI state
      this.$watch('$store.search.appliedFilters', (newAppliedFilters) => {
        this.syncFilterStates(newAppliedFilters);
      });


      
      // Initial setup if filters are already available
      if (this.$store.search.filters?.length > 0) {
        this.setupFilters(this.$store.search.filters);
      }
    },

    /**
     * Check if filters are available
     */
    get hasFilters() {
      return this.$store.search.filters?.length > 0 || !!this.$store.search.filtersLoading;
    },

    /**
     * Get current filters from store
     */
    get filters() {
      return this.$store.search.filters || [];
    },

    /**
     * Setup filters with initial state
     */
    setupFilters(filters) {
      if (!Array.isArray(filters)) return;
      
      // Initialize filter states based on applied filters
      this.syncFilterStates(this.$store.search.appliedFilters || []);
    },

    /**
     * Sync filter UI states with applied filters from store
     */
    syncFilterStates(_appliedFilters) {
      // This method ensures filter UI reflects the current applied state
      // The reactive templates will automatically update based on isValueSelected() calls
    },

    /**
     * Check if a filter has any active values
     */
    hasActiveValues(filter) {
      if (!filter || !filter.values) return false;
      
      return filter.values.some(value => this.isValueSelected(filter.id, value.id));
    },

    /**
     * Check if a filter has swatches
     */
    hasSwatches(filter) {
      if (!filter || !filter.values) return false;
      
      return filter.values.some(value => value.swatch || value.image);
    },

    /**
     * Get count of active values for a filter
     */
    getActiveCount(filter) {
      if (!filter || !filter.values) return 0;
      
      return filter.values.filter(value => this.isValueSelected(filter.id, value.id)).length;
    },

    /**
     * Check if a specific filter value is selected
     */
    isValueSelected(filterId, valueId) {
      const appliedFilters = this.$store.search.appliedFilters || [];
      
      return appliedFilters.some(appliedFilter => {
        return appliedFilter.id === filterId && 
               appliedFilter.values && 
               appliedFilter.values.some(val => val.id === valueId);
      });
    },

    /**
     * Toggle a filter value (checkbox behavior)
     */
    async toggleFilterValue(filterId, value) {
      try {
        const currentFilters = (this.$store.search.appliedFilters || []).map(filter => ({
          ...filter,
          values: (filter.values || []).map(filterValue => ({ ...filterValue }))
        }));
        const nextValue = { ...value };
        
        // Find existing filter
        let filterIndex = currentFilters.findIndex(f => f.id === filterId);
        
        if (filterIndex === -1) {
          // Create new filter entry
          const originalFilter = this.filters.find(f => f.id === filterId);
          currentFilters.push({
            id: filterId,
            label: originalFilter?.label || '',
            type: originalFilter?.type || 'LIST',
            values: [nextValue]
          });
        } else {
          // Update existing filter
          const filter = currentFilters[filterIndex];
          const valueIndex = filter.values.findIndex(v => v.id === nextValue.id);
          
          if (valueIndex === -1) {
            // Add value
            filter.values.push(nextValue);
          } else {
            // Remove value
            filter.values.splice(valueIndex, 1);
            
            // Remove filter if no values left
            if (filter.values.length === 0) {
              currentFilters.splice(filterIndex, 1);
            }
          }
        }
        
        // Apply filters to search store
        await this.$store.search.applyFilters(currentFilters);
        
      } catch (error) {
        console.error('[SearchFilters] Failed to toggle filter value:', error);
      }
    },

    // =====================================
    // Dropdown Management (Horizontal Layout)
    // =====================================

    /**
     * Check if dropdown is open
     */
    isDropdownOpen(filterId) {
      return this.openDropdowns.has(filterId);
    },

    /**
     * Toggle dropdown state
     */
    toggleDropdown(filterId) {
      if (this.openDropdowns.has(filterId)) {
        this.closeDropdown(filterId);
      } else {
        this.openDropdown(filterId);
      }
    },

    /**
     * Open dropdown
     */
    openDropdown(filterId) {
      // Close other dropdowns if needed (optional behavior)
      if (this.settings.closeOthersOnOpen) {
        this.closeAllDropdowns();
      }
      
      this.openDropdowns.add(filterId);
      
      // Dispatch event
      window.dispatchEvent(new CustomEvent('searchFilters:dropdownOpen', {
        detail: { filterId }
      }));
    },

    /**
     * Close dropdown
     */
    closeDropdown(filterId) {
      this.openDropdowns.delete(filterId);
      
      // Dispatch event
      window.dispatchEvent(new CustomEvent('searchFilters:dropdownClose', {
        detail: { filterId }
      }));
    },

    /**
     * Close all dropdowns
     */
    closeAllDropdowns() {
      this.openDropdowns.clear();
    },

    // =====================================
    // Accordion Management (Vertical Layout)
    // =====================================

    /**
     * Check if accordion is open
     */
    isAccordionOpen(filterId) {
      return this.openAccordions.has(filterId);
    },

    /**
     * Toggle accordion state
     */
    toggleAccordion(filterId) {
      if (this.openAccordions.has(filterId)) {
        this.closeAccordion(filterId);
      } else {
        this.openAccordion(filterId);
      }
    },

    /**
     * Open accordion
     */
    openAccordion(filterId) {
      // Close others if not allowing multiple
      if (!this.settings.accordion_allow_multiple) {
        this.closeAllAccordions();
      }
      
      this.openAccordions.add(filterId);
    },

    /**
     * Close accordion
     */
    closeAccordion(filterId) {
      this.openAccordions.delete(filterId);
    },

    /**
     * Close all accordions
     */
    closeAllAccordions() {
      this.openAccordions.clear();
    },

    // =====================================
    // Price Range Management
    // =====================================

    /**
     * Get minimum value for range filter
     */
    getRangeMin(filter) {
      const appliedFilter = this.$store.search.appliedFilters?.find(f => f.id === filter.id);
      if (appliedFilter?.values?.[0]?.input) {
        try {
          const input = JSON.parse(appliedFilter.values[0].input);
          return input.price?.min || this.getFilterRangeMin(filter);
        } catch (_error) {
          return this.getFilterRangeMin(filter);
        }
      }
      return this.getFilterRangeMin(filter);
    },

    /**
     * Get maximum value for range filter
     */
    getRangeMax(filter) {
      const appliedFilter = this.$store.search.appliedFilters?.find(f => f.id === filter.id);
      if (appliedFilter?.values?.[0]?.input) {
        try {
          const input = JSON.parse(appliedFilter.values[0].input);
          return input.price?.max || this.getFilterRangeMax(filter);
        } catch (_error) {
          return this.getFilterRangeMax(filter);
        }
      }
      return this.getFilterRangeMax(filter);
    },

    /**
     * Get filter's minimum possible value
     */
    getFilterRangeMin(filter) {
      if (filter?.values?.[0]?.input) {
        try {
          const input = JSON.parse(filter.values[0].input);
          return input.price?.min || 0;
        } catch (_error) {
          return 0;
        }
      }
      return 0;
    },

    /**
     * Get filter's maximum possible value
     */
    getFilterRangeMax(filter) {
      if (filter?.values?.[0]?.input) {
        try {
          const input = JSON.parse(filter.values[0].input);
          return input.price?.max || 1000;
        } catch (_error) {
          return 1000;
        }
      }
      return 1000;
    },

    /**
     * Update range minimum value
     */
    async updateRangeMin(filter, minValue) {
      const min = parseFloat(minValue) || this.getFilterRangeMin(filter);
      const max = this.getRangeMax(filter);
      await this.updatePriceRange(filter, min, max);
    },

    /**
     * Update range maximum value
     */
    async updateRangeMax(filter, maxValue) {
      const min = this.getRangeMin(filter);
      const max = parseFloat(maxValue) || this.getFilterRangeMax(filter);
      await this.updatePriceRange(filter, min, max);
    },

    /**
     * Update price range filter
     */
    async updatePriceRange(filter, min, max) {
      try {
        const currentFilters = [...(this.$store.search.appliedFilters || [])];
        
        // Remove existing price filter
        const filteredFilters = currentFilters.filter(f => f.id !== filter.id);
        
        // Add new price filter if values are different from defaults
        const defaultMin = this.getFilterRangeMin(filter);
        const defaultMax = this.getFilterRangeMax(filter);
        
        if (min !== defaultMin || max !== defaultMax) {
          const priceFilter = {
            id: filter.id,
            label: filter.label,
            type: 'PRICE_RANGE',
            values: [{
              id: filter.id,
              label: `${min} - ${max}`,
              input: JSON.stringify({ price: { min, max } })
            }]
          };
          
          filteredFilters.push(priceFilter);
        }
        
        // Apply filters
        await this.$store.search.applyFilters(filteredFilters);
        
      } catch (error) {
        console.error('[SearchFilters] Failed to update price range:', error);
      }
    },

    // =====================================
    // Custom Swatch Integration
    // =====================================

    /**
     * Normalize string to prevent mismatches from special characters and spacing
     */
    normalizeString(str) {
      if (!str) return '';
      return str
        .replace(/&amp;/g, '&')
        .replace(/&#39;/g, "'")
        .replace(/&rsquo;/g, "'")
        .replace(/[\u2018\u2019\u0060\u00B4]/g, "'") // Normalize all types of apostrophes to standard '
        .replace(/\s+/g, ' ') // Collapse multiple spaces
        .trim()
        .toLowerCase();
    },

    /**
     * Initialize custom swatch cache by fetching metaobject entries
     */
    async initCustomSwatchCache() {
      try {

        const metaobjectEntries = await this.fetchMetaobjectEntries(this.customSwatchMetaobject);
        
        // Build hashmap with title as key and circular_image as value
        this.metaobjectSwatchCache.clear();
        
        metaobjectEntries.forEach(entry => {
          const title = entry.fields?.find(field => field.key === 'title')?.value;
          const circularImage = entry.fields?.find(field => field.key === 'circular_image')?.reference?.image;
          
          if (title && circularImage) {
            this.metaobjectSwatchCache.set(this.normalizeString(title), circularImage);
          }
        });
        
      } catch (error) {
        console.error('[SearchFilters] Failed to initialize custom swatch cache:', error);
      }
    },

    /**
     * Fetch all metaobject entries via Storefront API with cursor-based pagination
     */
    async fetchMetaobjectEntries(metaobjectHandle) {
      const accessToken = window.themeSettings?.storefront_access_token;

      if (!accessToken) {
        throw new Error('Storefront access token not found in window.themeSettings');
      }

      const PAGE_SIZE = 250; // Storefront API hard cap per page

      const query = `
        query GetMetaobjectEntries($handle: String!, $first: Int!, $after: String) {
          metaobjects(type: $handle, first: $first, after: $after) {
            edges {
              node {
                id
                handle
                fields {
                  key
                  value
                  reference {
                    ... on MediaImage {
                      image {
                        url
                        altText
                        width
                        height
                      }
                    }
                  }
                }
              }
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      `;

      const fetchPage = async (after) => {
        const response = await fetch('/api/2023-07/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': accessToken
          },
          body: JSON.stringify({
            query,
            variables: { handle: metaobjectHandle, first: PAGE_SIZE, after: after || null }
          })
        });

        if (!response.ok) {
          throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();

        if (data.errors) {
          throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
        }

        return data.data?.metaobjects;
      };

      const allEntries = [];
      let after = null;

      do {
        const page = await fetchPage(after);
        allEntries.push(...(page?.edges?.map(edge => edge.node) || []));
        after = page?.pageInfo?.hasNextPage ? page.pageInfo.endCursor : null;
      } while (after);

      return allEntries;
    },

    /**
     * Get custom swatch image for a filter value
     */
    getCustomSwatchImage(filterId, valueLabel) {
      // Only return custom swatch for the configured filter
      if (filterId !== this.customSwatchFilterId || typeof valueLabel !== 'string') {
        return null;
      }

      // Look up the image in the cache using normalized string comparison
      const image = this.metaobjectSwatchCache.get(this.normalizeString(valueLabel));
      
      if (image) {
        return image;
      }
      return null;
    },

    /**
     * Check if a filter uses custom swatches
     */
    hasCustomSwatches(filter) {
      return Boolean(filter?.id && this.customSwatchFilterId && filter.id === this.customSwatchFilterId);
    }
  };
};

// Export for Alpine.js auto-loading
export default {
  name: 'searchFilters',
  component: searchFilters
};
